/* global window */
import React, { PureComponent } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { updateDimensions, shouldChangeArtboard, updateArtboard } from 'PreviewActions';
import { getMetadata, getSelectedAdstate } from 'Selectors';
import DraggableWrapper from 'Components/DraggableWrapper/DraggableWrapper';
import ResizeHandle from 'PreviewComponents/ResizableContainer/ResizeHandle';
import ArtboardDetails from 'PreviewComponents/ResizableContainer/ArtboardDetails';
import DeviceImage from 'PreviewComponents/ResizableContainer/DeviceImage';
import { colourCanvas, colourMasterLighter } from 'Styles/colors.css';
import { RevealContainer, Revealhelper } from 'PreviewComponents/ResizableContainer/RevealContainer';
import { LoadingSpinner } from 'PreviewComponents/ResizableContainer/LoadingSpinner';

import {
  RESIZE_MULTIPLIER,
  SETTINGS_PANEL_WIDTH,
  BOUND_PADDING,
  DEVICE_ORIENTATION,
  FIT_TO_SCREEN,
  CANVAS_SCALE,
  MAX_HEIGHT,
  DIMENSIONS,
  MAX_WIDTH,
  DEVICE,
  BOUNDS,
  FRAME,
  IMAGE,
} from 'PreviewConstants/';
import {
  TOPBAR_HEIGHT,
  HEIGHT,
  WIDTH,
  UI,
} from 'SharedConstants/';

const DOUBLE_BOUND_PADDING = BOUND_PADDING * 2;
const SCROLL_PADDING = 40;
const EMPTY_OBJECT = {};

const defaultCanvasSize = {
  // width: window.innerWidth,
  // height: window.innerHeight - TOPBAR_HEIGHT,
  width: `100%`,
  height: `100%`,
};

const CanvasContainer = styled.div`
  width: ${defaultCanvasSize.width};
  height: calc(100% - ${TOPBAR_HEIGHT}px);
  margin-top: ${TOPBAR_HEIGHT}px;
  overflow: ${props => (
    props.fitToScreen ? 'hidden' : 'scroll'
  )};
`;

const LeftAlignedCanvasStyles = ({ left = 0, right = 0 }) =>
  `
    padding-left: ${left + SCROLL_PADDING}px;
    padding-right: ${right + SCROLL_PADDING}px;
    justify-content: flex-start;
  `;

const TopAlignedCanvasStyles = ({ top = 0, bottom = 0 }) =>
  `
    padding-top: ${top + SCROLL_PADDING}px;
    padding-bottom: ${bottom + SCROLL_PADDING}px;
    align-items: flex-start;
  `;

const Canvas = styled.div`
  display: flex;
  flex-direction: column;
  width: ${props => props.canvasWidth};
  height: ${props => props.canvasHeight};
  padding-left: 0;
  padding-right: 0;
  justify-content: center;
  padding-top: 0;
  padding-bottom: 0;
  align-items: center;
  background-color: ${colourCanvas}
  ${props => props.alignLeft && LeftAlignedCanvasStyles(props.deviceFrame)}
  ${props => props.alignTop && TopAlignedCanvasStyles(props.deviceFrame)}
`;

const ArtboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: '10';
`;

const Row = styled.div`
  display: flex;
  position: relative;
`;

// const ResizeContainer = styled.div`
//   overflow: hidden;
//   background-color: ${colourMasterLighter};
// `;

const AdContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  align-items: center;
  transform: translateZ(0px);
`;

const ResizeDefaultHandle = styled.div`
  display: flex;
  opacity: ${props => (
    props.disabled ? 0 : 0.7
  )};
  pointer-events: ${props => (
    props.disabled ? 'none' : 'auto'
  )}
`;

const ResizeWidthHandle = styled(ResizeDefaultHandle)`
  width: 30px;
  padding-left: 10px;
  align-items: center;
`;

const ResizeHeightHandle = styled(ResizeDefaultHandle)`
  height: 15px;
  padding-top: 10px;
  justify-content: center;
`;

const ResponsiveContainer = ({ artboardName, width, height, isDevice, children }) =>
  (<div
    data-artboard={artboardName}
    style={{
      width,
      height,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: colourMasterLighter,
      zIndex: '10',
    }}
  >
    <LoadingSpinner isDevice={isDevice} revealFormat={false} width={width} height={height} />
    {children}
  </div>
  );

ResponsiveContainer.propTypes = {
  children: PropTypes.element.isRequired,
  artboardName: PropTypes.string.isRequired, // eslint-disable-line
  width: PropTypes.string.isRequired, // eslint-disable-line
  height: PropTypes.string.isRequired, // eslint-disable-line
};

class ResizableContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { adLoaded: false };
  }
  componentDidMount() {
    const iframeWindow = window; // document.querySelector('iframe').contentWindow;
    const adLoadInterval = setInterval(() => {
      if (!iframeWindow['wve-api']) return;
      const spinner = document.getElementById('loading-spinner');
      if (spinner) spinner.parentNode.removeChild(spinner);
      clearInterval(adLoadInterval);
      this.setState({ adLoaded: true });
    });
    document.addEventListener('artboard-change', this.handleChange, false);
  }
  componentDidUpdate(prevProps) {
    if (
        prevProps.dimensions !== this.props.dimensions
      ||
        prevProps.fitToScreen !== this.props.fitToScreen
    ) {
      shouldChangeArtboard();
    }
  }
  resizeAd = (e, ui, dimension) => {
    const { deltaX, deltaY } = ui;
    const {
      dimensions,
      canvasScale,
      // eslint-disable-next-line no-shadow
      updateDimensions,
    } = this.props;

    const isWidth = dimension === WIDTH;
    const delta = isWidth ? deltaX : deltaY;
    const multiplier = !isWidth ? RESIZE_MULTIPLIER : 1;
    const scaledDelta = (delta * multiplier) / canvasScale;
    const newValue = dimensions.get(dimension) + scaledDelta;
    updateDimensions({ [dimension]: newValue });
  };
  handleChange = (evt) => {
    const { updateArtboard } = this.props;
    const { rootElement, artboards } = store.getState();
    const artboardName = rootElement.parentNode.getAttribute('data-artboard');
    const selectedArtboardKey = artboards.find(a => a.name === artboardName).key;
    updateArtboard(selectedArtboardKey);
  };
  axisWillScroll = (dimensionForAxis) => {
    const { dimensions, fitToScreen, device, deviceOrientation } = this.props;
    const minCanvasSize = defaultCanvasSize[dimensionForAxis] - DOUBLE_BOUND_PADDING;
    const height = device ? device.getIn([deviceOrientation, IMAGE, dimensionForAxis]) : dimensions.get(dimensionForAxis);
    const heightMoreThenCanvas = height > minCanvasSize;
    return !fitToScreen && heightMoreThenCanvas;
  }
  render() {
    const {
      props: {
        revealFormat,
        artboardName,
        children,
        dimensions,
        fitToScreen,
        canvasScale,
        reloadAd,
        device,
        isDevice,
        deviceOrientation,
      },
      state: {
        adLoaded,
      },
      resizeAd,
      axisWillScroll,
    } = this;

    const dimensionsWidth = dimensions.get(WIDTH);
    const dimensionsHeight = dimensions.get(HEIGHT);

    const width = `${dimensionsWidth * canvasScale}px`;
    const height = `${dimensionsHeight * canvasScale}px`;

    const xWillScroll = axisWillScroll(WIDTH);
    const yWillScroll = axisWillScroll(HEIGHT);
    const canvasWidth = xWillScroll ? dimensionsWidth + DOUBLE_BOUND_PADDING : defaultCanvasSize.width;
    const canvasHeight = yWillScroll ? dimensionsHeight + DOUBLE_BOUND_PADDING : defaultCanvasSize.height;

    const disableHandles = !adLoaded || !fitToScreen || device || isDevice;
    const showReszieHandles = !disableHandles;

    const ResizeContainer = revealFormat ? RevealContainer : ResponsiveContainer;

    return (
      <CanvasContainer
        fitToScreen={fitToScreen}
      >
        <Canvas
          canvasWidth={`${canvasWidth}`}
          canvasHeight={`${canvasHeight}`}
          alignLeft={xWillScroll}
          alignTop={yWillScroll}
          deviceFrame={device ? device.getIn([deviceOrientation, FRAME]) : EMPTY_OBJECT}
        >
          <ArtboardContainer>

            <ArtboardDetails
              reloadAd={reloadAd}
              adLoaded={adLoaded}
            />

            <Row>
              <ResizeContainer
                artboardName={artboardName}
                width={width}
                height={height}
                isDevice={isDevice}
              >
                {children}
              </ResizeContainer>
              {device && <DeviceImage />}

              {showReszieHandles &&
                <DraggableWrapper
                  onDrag={(e, ui) => resizeAd(e, ui, WIDTH)}
                  axis="x"
                >
                  <ResizeWidthHandle
                    disabled={disableHandles}
                    style={{ height }}
                  >
                    <ResizeHandle />
                  </ResizeWidthHandle>
                </DraggableWrapper>
              }
            </Row>

            <DraggableWrapper
              onDrag={(e, ui) => resizeAd(e, ui, HEIGHT)}
              axis="y"
            >
              <ResizeHeightHandle
                disabled={disableHandles}
                style={{ width }}
              >
                <ResizeHandle axis="y" />
              </ResizeHeightHandle>
            </DraggableWrapper>

          </ArtboardContainer>

        </Canvas>
      </CanvasContainer>
    );
  }
}

ResizableContainer.propTypes = {
  revealFormat: PropTypes.bool.isRequired,
  artboardName: PropTypes.string.isRequired,
  dimensions: PropTypes.instanceOf(Map).isRequired,
  maxWidth: PropTypes.number.isRequired,
  maxHeight: PropTypes.number.isRequired,
  children: PropTypes.node,
  updateDimensions: PropTypes.func.isRequired,
  updateArtboard: PropTypes.func.isRequired,
  canvasScale: PropTypes.number.isRequired,
  fitToScreen: PropTypes.bool.isRequired,
  reloadAd: PropTypes.func.isRequired,
  device: PropTypes.instanceOf(Map),
  isDevice: PropTypes.bool.isRequired,
  deviceOrientation: PropTypes.string.isRequired,
};

ResizableContainer.defaultProps = {
  children: null,
  device: null,
};

const mapStateToProps = (state) => {
  const adState = getSelectedAdstate(state);
  return {
    revealFormat: getMetadata(state).get('revealFormat') || getMetadata(state).get('formatName') === 'Scroller',
    artboardName: adState.name,
    dimensions: state.getIn([UI, DIMENSIONS]),
    maxWidth: state.getIn([UI, BOUNDS, MAX_WIDTH]),
    maxHeight: state.getIn([UI, BOUNDS, MAX_HEIGHT]),
    canvasScale: state.getIn([UI, CANVAS_SCALE]),
    fitToScreen: state.getIn([UI, FIT_TO_SCREEN]),
    device: state.getIn([UI, DEVICE]),
    deviceOrientation: state.getIn([UI, DEVICE_ORIENTATION]),
  };
};

const mapDispatchToProps = dispatch => ({
  updateDimensions: dimensions =>
    dispatch(updateDimensions(dimensions)),
  updateArtboard: artboard =>
    dispatch(updateArtboard(artboard)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResizableContainer);



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/components/ResizableContainer/ResizableContainer.jsx